import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchAlbumListAsync } from '../../store/albumSlice';
import { setGlobalDialogTips } from '../../store/memberSlice';
import {
    createAlbum,
    renameAlbum,
    deleteAlbum
} from '../../api/boardManage';
import { useMatomoTrackingPoint } from '../../hooks/useMatomoTrackingPoint';
import { useThemeImage } from '../../hooks/useThemeImage';
import './Album.scss';

interface AlbumProps {
    onAlbumClick?: (albumId: string) => void;
}

export default function Album({ onAlbumClick }: AlbumProps) {
    const dispatch: AppDispatch = useDispatch();
    const albums = useSelector((state: RootState) => state.album.albumList);
    const [collapsed, setCollapsed] = useState(false);

    const [menuAnchor, setMenuAnchor] = useState<{ albumId: string; top: number; left: number } | null>(null);

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameAlbumId, setRenameAlbumId] = useState('');
    const [renameValue, setRenameValue] = useState('');
    const [deleteDialogMode, setDeleteDialogMode] = useState<'confirm' | 'warning' | null>(null);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

    useEffect(() => {
        dispatch(fetchAlbumListAsync());
    }, [dispatch]);

    const { trackAlbumCreate, trackAlbumDelete, trackAlbumDeleteConfirm, trackFolderEnterClick } = useMatomoTrackingPoint();

    const albumCreateIcon = useThemeImage('albumCreate');
    const albumBaseImage = useThemeImage('albumBase');

    const handleCreateAlbum = async () => {
        try {
            const result = await createAlbum('未命名画册');
            if (result) {
                dispatch(fetchAlbumListAsync());
                trackAlbumCreate({ page_id: window.location.href });
            }
        } catch {
        }
    };

    const handleRenameAlbum = async () => {
        if (!renameValue.trim()) return;
        try {
            const result = await renameAlbum(renameAlbumId, renameValue.trim());
            if (result) {
                setRenameDialogOpen(false);
                setMenuAnchor(null);
                dispatch(fetchAlbumListAsync());
            }
        } catch {
        }
    };

    const handleDeleteAlbum = async () => {
        if (!menuAnchor) return;
        trackAlbumDelete({ page_id: window.location.href, format_id: menuAnchor.albumId });
        const album = albums.find(a => a.albumId === menuAnchor.albumId);
        if (album && album.boardCount > 0) {
            setDeleteTargetId(menuAnchor.albumId);
            setDeleteDialogMode('warning');
            setMenuAnchor(null);
            return;
        }
        setDeleteTargetId(menuAnchor.albumId);
        setDeleteDialogMode('confirm');
        setMenuAnchor(null);
    };

    const confirmDeleteAlbum = async () => {
        if (deleteDialogMode === 'warning') {
            setDeleteDialogMode(null);
            return;
        }
        if (!deleteTargetId) return;
        try {
            await deleteAlbum(deleteTargetId);
            trackAlbumDeleteConfirm({ page_id: window.location.href, format_id: deleteTargetId, select_content: 'delete_confirm', switch_status: 'confirm' });
            setDeleteDialogMode(null);
            setDeleteTargetId(null);
            dispatch(fetchAlbumListAsync());
        } catch {
            setDeleteDialogMode(null);
            setDeleteTargetId(null);
        }
    };

    const handleMoreClick = (e: React.MouseEvent, albumId: string) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setMenuAnchor({
            albumId,
            top: rect.bottom + 4,
            left: rect.left + rect.width - 156
        });
    };

    const handleMenuRename = () => {
        if (!menuAnchor) return;
        const album = albums.find(a => a.albumId === menuAnchor.albumId);
        setRenameAlbumId(menuAnchor.albumId);
        setRenameValue(album?.albumName || '');
        setRenameDialogOpen(true);
    };

    const handleClickOutside = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    useEffect(() => {
        if (menuAnchor) {
            const handler = () => handleClickOutside();
            document.addEventListener('click', handler);
            return () => document.removeEventListener('click', handler);
        }
    }, [menuAnchor, handleClickOutside]);

    return (
        <div className="album-page">
            {/* 顶部标题栏 */}
            <div className="album-header" onClick={() => setCollapsed(!collapsed)}>
                <h1 className="album-title">画册</h1>
                {albums.length > 0 && <span className="album-total">{albums.length}</span>}
                <KeyboardArrowDownIcon className={`album-title-arrow ${collapsed ? 'arrow-right' : 'arrow-down'}`} />
            </div>

            {/* 画册网格 */}
            <div className={`album-grid-wrapper ${collapsed ? 'album-collapsed' : ''}`}>
                <div className="album-grid">
                    {/* 创建新画册卡片 */}
                    <div className="album-card album-card-create" style={{backgroundColor: 'transparent'}} onClick={handleCreateAlbum}>
                        <img src={albumCreateIcon} alt="创建新画册" className="create-icon" />
                    </div>

                    {/* 画册卡片列表 */}
                    {albums.map((album) => {
                        const coverSlots = album.covers.slice(0, 4);

                        return (
                            <div className="album-card-template" key={album.albumId} onClick={() => { onAlbumClick?.(album.albumId); trackFolderEnterClick({ page_id: window.location.href, format_id: album.albumId }); }}>
                                <div className="album-card-template-bg"></div>
                                {[0, 1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        className="album-card-template-fan"
                                        style={coverSlots[i] ? { backgroundImage: `url(${coverSlots[i]})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                                    />
                                ))}
                                <div className="album-card-template-info">
                                    <div className="album-card-template-count">{album.boardCount} 个绘板</div>
                                    <div className="album-card-template-name-row">
                                        <span className="album-card-template-name">{album.albumName}</span>
                                        <span className="album-card-template-more" onClick={(e) => { e.stopPropagation(); handleMoreClick(e, album.albumId); }}>
                                            <MoreVertIcon sx={{ fontSize: 18, color: 'var(--c-text-primary)' }} />
                                        </span>
                                    </div>
                                </div>
                                <img src={albumBaseImage} alt="" className="album-card-template-base" />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 下拉菜单弹窗 */}
            {menuAnchor && (
                <div
                    className="dropdown-menu"
                    style={{
                        position: 'fixed',
                        top: menuAnchor.top,
                        left: menuAnchor.left,
                        zIndex: 1300
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="dropdown-menu-item" onClick={handleMenuRename}>
                        <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 18, color: 'var(--c-text-primary)' }} />
                        <span>重命名</span>
                    </div>
                    <div className="dropdown-menu-item" onClick={handleDeleteAlbum}>
                        <DeleteOutlineIcon sx={{ fontSize: 18, color: 'var(--c-text-primary)' }} />
                        <span>删除画册</span>
                    </div>
                </div>
            )}

            {/* 重命名弹窗 */}
            <Dialog
                open={renameDialogOpen}
                onClose={() => setRenameDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>重命名画册</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder="请输入新名称"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameAlbum();
                        }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialogOpen(false)}>取消</Button>
                    <Button
                        variant="contained"
                        onClick={handleRenameAlbum}
                        disabled={!renameValue.trim()}
                    >
                        确定
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 删除确认/警告弹窗 */}
            <Dialog
                open={deleteDialogMode !== null}
                onClose={() => setDeleteDialogMode(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>删除画册</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteDialogMode === 'warning'
                            ? `当前画册内存有 ${albums.find(a => a.albumId === deleteTargetId)?.boardCount || 0} 个绘板，无法删除画册，请先删除或移出全部绘板后重试。`
                            : `确定要删除 ${(albums.find(a => a.albumId === deleteTargetId)?.albumName) || ''} ？ 此操作无法撤销。`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        if (deleteDialogMode === 'warning') {
                            trackAlbumDeleteConfirm({ page_id: window.location.href, format_id: deleteTargetId ?? '', select_content: 'delete_warning', switch_status: 'confirm' });
                        } else {
                            trackAlbumDeleteConfirm({ page_id: window.location.href, format_id: deleteTargetId ?? '', select_content: 'delete_confirm', switch_status: 'cancel' });
                        }
                        setDeleteDialogMode(null);
                    }}>{deleteDialogMode === 'warning' ? '我知道了' : '取消'}</Button>
                    {deleteDialogMode === 'confirm' && <Button onClick={confirmDeleteAlbum} color="error">删除</Button>}
                </DialogActions>
            </Dialog>

        </div>
    );
}
